import React, {useMemo} from 'react';
import { connect } from "react-redux";
import {
  SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS,
  SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS,
  SIDEBAR_NAVIGATION_BATHROOM_CABINET_BUTTONS,
  SIDEBAR_NAVIGATION_CABINET_BUTTONS,
} from "../../../../constants";
import { injectIntl } from "react-intl";
import $ from "jquery";
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

import {
  addChangeValuePopup,
  setSQFTActiveButton,
  setSQFTMenuOpened
} from "../../../../../../actions/sqftMenuFunctions";
import config from "../../../canvas/store/sqftConfig";
import {
  handleResetClick,
  handleResizeClick,
  handleRotateClick,
  handleSpacerClick,
  handleWallClick,
  is3DMode,
  resetEvents
} from "../../NavigationHelper";
import useFetchFeatures from "../../../../hocs/useFetchFeatures";
import LoadingSpinner from "../../../../../ui/loadingSpinner";
import { getIsBathroom } from "../../../../../kvComponent/navigation/countertopSwitchers";
import { getCurrentLayout } from "../../../../../appLayout";
import { getFeatureGroupName } from "../../../../../../misc/helpers";
import {
  hideSqftTooltip,
  showSqftTooltip
} from "../../../../../../actions/sqftTooltipFunctions";
import { getButtonAvailability } from "../helpers/getButtonAvailability";

/**
 * Dependency ids are used for:
 * 1) Fetching features (we will read only those deps that exist in featuresData)
 * 2) Wizard gating (if wizard is active, at least one wizardSelectedValues.partId must match deps)
 */
const CABINET_MENU_BUTTON_DEPS = {
  [SIDEBAR_NAVIGATION_CABINET_BUTTONS.lower]: ["bottom_cabinet", "center_island_cabinet"],
  [SIDEBAR_NAVIGATION_CABINET_BUTTONS.upper]: ["top_cabinet"],
  [SIDEBAR_NAVIGATION_CABINET_BUTTONS.tall]: ["tall_cabinet"],
  [SIDEBAR_NAVIGATION_CABINET_BUTTONS.centerIsland]: ["center_island_countertop", "countertop"],
  [SIDEBAR_NAVIGATION_CABINET_BUTTONS.handles]: ["handle", "top_handle", "bottom_handle"],
  [SIDEBAR_NAVIGATION_CABINET_BUTTONS.hinge]: ["hinge"],
  [SIDEBAR_NAVIGATION_CABINET_BUTTONS.crown_molding]: ["crown_molding"],
  [SIDEBAR_NAVIGATION_CABINET_BUTTONS.end_panel]: ["wall_door_end_accessory", "base_door", "island_panel", "refridgerator_panel"],
  [SIDEBAR_NAVIGATION_BATHROOM_CABINET_BUTTONS.vanity]: ["vanity_cabinet_boxes", "vanitie_cabinet_boxes", "vanity_boxes"],

  // 3D cabinet buttons
  [SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS.bottom_cabinet]: ["bottom_cabinet"],
  [SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS.top_cabinet]: ["top_cabinet"],
  [SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS.tall_cabinet]: ["tall_cabinet"],

  // 3D menu can also show handles/centerIsland
  [SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS.handles]: ["handle", "top_handle", "bottom_handle"],
  [SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS.centerIsland]: ["center_island_countertop", "countertop"],

  // Cabinet-box groups determine whether their add menus have usable 3D models.
  [SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.vanity_cabinet]: ["vanity_cabinet"],
  [SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.top_cabinet]: ["top_cabinet"],
  [SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.linen_vanity_cabinet]: [
    "linen_vanity_cabinet",
  ],
  [SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.vanity_cabinet_boxes]: [
    "vanity_cabinet_boxes",
    "vanitie_cabinet_boxes",
    "vanity_boxes",
  ],
  [SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.top_cabinet_boxes]: ["top_cabinet_boxes"],
  [SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.linen_cabinet_boxes]: [
    "linen_cabinet_boxes",
    "linen_vanity_boxes",
  ],
  [SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.vanity_countertop]: ["vanity_countertop"],
};

/**
 * Collect all unique deps to fetch once.
 * useFetchFeatures will return featuresData; we will only read keys that exist.
 */
const CABINET_MENU_FETCH_KEYS = Array.from(
  new Set(Object.values(CABINET_MENU_BUTTON_DEPS).flat())
);

const BATHROOM_CABINET_BUTTON_LABELS = {
  [SIDEBAR_NAVIGATION_BATHROOM_CABINET_BUTTONS.vanity]: {
    id: "sqft.navigation.button.vanity_boxes",
    defaultMessage: "Vanity Boxes",
  },
  [SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.vanity_cabinet]: {
    id: "sqft.navigation.button.cabinet_style",
    defaultMessage: "Vanity Style",
  },
  [SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.top_cabinet]: {
    id: "sqft.navigation.button.wall_style",
    defaultMessage: "Wall Style",
  },
  [SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.linen_vanity_cabinet]: {
    id: "sqft.navigation.button.linen_style",
    defaultMessage: "Linen Style",
  },
  [SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.vanity_cabinet_boxes]: {
    id: "sqft.navigation.button.vanity_boxes",
    defaultMessage: "Vanity Boxes",
  },
  [SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.top_cabinet_boxes]: {
    id: "sqft.navigation.button.wall_boxes",
    defaultMessage: "Wall Boxes",
  },
  [SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.linen_cabinet_boxes]: {
    id: "sqft.navigation.button.linen_boxes",
    defaultMessage: "Linen Boxes",
  },
};



const SqftToolMenuCabinet = (props) => {
  const {
    activeButton,
    menuOpened,
    setSQFTActiveButton,
    setSQFTMenuOpened,
    fabricatorData,
    intl,
    showSqftTooltip,
    hideSqftTooltip,
    wizardIsActive
  } = props;

  const isThreeDMode = is3DMode();
  let buttons = getIsBathroom()
    ? isThreeDMode
      ? { ...SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS }
      : { ...SIDEBAR_NAVIGATION_BATHROOM_CABINET_BUTTONS }
    : isThreeDMode
      ? { ...SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS }
      : { ...SIDEBAR_NAVIGATION_CABINET_BUTTONS };

  const expanded = activeButton
    && menuOpened !== "bumpout"
    && (menuOpened === "move" || Object.keys(buttons).some(buttonKey => menuOpened === buttonKey));

  const partIds = useMemo(() => CABINET_MENU_FETCH_KEYS, []);
  const { featuresData, isLoading } = useFetchFeatures(
    partIds,
    "",
    {},
    isThreeDMode,
    false,
    true,
    true
  );

  // Stores per-button state
  const buttonStates = {};
  const buttonDisableReasons = {};

  if (!isLoading && featuresData) {
    buttons = Object.keys(buttons).reduce((newButtons, buttonKey) => {
      let shouldRemove = false;
      let isDisabled = false;
      let disableReason = "not_available";

      const deps = CABINET_MENU_BUTTON_DEPS[buttonKey];
      if (deps?.length) {
        const availability = getButtonAvailability({
          featuresData,
          deps,
          wizardIsActive,
          useAllWizards: true,
        });

        shouldRemove = availability.shouldRemove;
        isDisabled = availability.isDisabled;
        disableReason = availability.disableReason;
      }

      if (shouldRemove) {
        return newButtons;
      }

      buttonStates[buttonKey] = isDisabled;
      buttonDisableReasons[buttonKey] = disableReason;
      newButtons[buttonKey] = buttons[buttonKey];

      return newButtons;
    }, {});
  }

  const handleButtonClick = (buttonKey) => {
    if (buttonStates[buttonKey]) return;

    setSQFTMenuOpened(buttonKey);
    setSQFTActiveButton(buttonKey);
    config.workspace.menuItemActive = buttonKey;
    resetEvents();

    switch (buttonKey) {
      case SIDEBAR_NAVIGATION_CABINET_BUTTONS.move:
        setSQFTMenuOpened('');
        break;
      case SIDEBAR_NAVIGATION_CABINET_BUTTONS.resize:
        setSQFTMenuOpened('');
        setSQFTActiveButton(buttonKey);
        handleResizeClick();
        break;
      case SIDEBAR_NAVIGATION_CABINET_BUTTONS.rotate:
        handleRotateClick();
        break;
      case SIDEBAR_NAVIGATION_CABINET_BUTTONS.spacer:
        setSQFTMenuOpened('');
        setSQFTActiveButton(buttonKey);
        handleSpacerClick();
        break;
      case SIDEBAR_NAVIGATION_CABINET_BUTTONS.wall:
        setSQFTActiveButton(buttonKey);
        handleWallClick();
        break;
      case SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS.top_cabinet:
      case SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS.bottom_cabinet:
      case SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS.tall_cabinet:
      case SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.linen_vanity_cabinet:
      case SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS.upper_cabinet_boxes:
      case SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS.lower_cabinet_boxes:
      case SIDEBAR_NAVIGATION_3D_CABINET_BUTTONS.tall_cabinet_boxes:
      case SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.vanity_cabinet_boxes:
      case SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.top_cabinet_boxes:
      case SIDEBAR_NAVIGATION_3D_BATHROOM_CABINET_BUTTONS.linen_cabinet_boxes:
        setSQFTMenuOpened(buttonKey);
        break;
      case 'reset':
        handleResetClick();
        break;
    }

    $('.lightbox-option-selected').removeClass('lightbox-option-selected');
  };

  const handleMouseEnter = (event, buttonKey) => {
    if (!buttonStates[buttonKey]) return;

    const targetRect = event.currentTarget.getBoundingClientRect();
    const featureName = getButtonName(buttonKey);
    const reason = buttonDisableReasons[buttonKey];

    const titleId = reason === "missing_3d_models"
      ? "sqft.navigation.missing_3d_models_title"
      : "sqft.navigation.not_available_title";

    const descId = reason === "missing_3d_models"
      ? "sqft.navigation.missing_3d_models_description"
      : "sqft.navigation.not_available_description";

    showSqftTooltip({
      title: `${featureName} ${intl.formatMessage({ id: titleId })}`,
      description: `${featureName} ${intl.formatMessage({ id: descId })}`,
      icon: <AutoFixHighIcon />,
      position: 'right',
      targetRect
    });
  };

  const handleMouseLeave = () => {
    hideSqftTooltip();
  };

  const getButtonName = (buttonKey) => {
    const bathroomButtonLabel = BATHROOM_CABINET_BUTTON_LABELS[buttonKey];
    if (getIsBathroom() && bathroomButtonLabel) {
      return intl.formatMessage(bathroomButtonLabel);
    }

    const layout = getCurrentLayout();
    const featureGroup = layout.getFeatureGroupByPartId(buttonKey);
    if (featureGroup) {
      return getFeatureGroupName(featureGroup, intl, fabricatorData);
    }
    return intl.formatMessage({ id: "sqft.navigation.submenu." + buttonKey });
  };

  return (
    <nav className={"navigation side" + (expanded ? " expanded" : "")} data-side="navigation">
      <div className="navigation-list">
        {isLoading
          ? <LoadingSpinner />
          : !expanded ? null : Object.keys(buttons).map((buttonKey) => {
            const isDisabled = buttonStates[buttonKey];

            return (
              <button
                key={buttonKey}
                className={
                  "animated fadeInUp button button--" + buttonKey +
                  (isDisabled ? " button--disabled" : "")
                }
                onClick={() => handleButtonClick(buttonKey)}
                onMouseEnter={(e) => handleMouseEnter(e, buttonKey)}
                onMouseLeave={handleMouseLeave}
              >
                <span className="button-text">
                  {getButtonName(buttonKey)}
                </span>
              </button>
            );
          })}
      </div>
    </nav>
  );
};

const mapStateToProps = (store) => ({
  menuOpened: store.sqftMenuState.menuOpened,
  activeButton: store.sqftMenuState.activeButton,
  fabricatorData: store.fabricatorState,
  wizardIsActive: store.wizardState.wizardIsActive,
});

const mapDispatchToProps = {
  setSQFTMenuOpened,
  setSQFTActiveButton,
  addChangeValuePopup,
  showSqftTooltip,
  hideSqftTooltip,
};

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(SqftToolMenuCabinet)
);
